import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBTutorialSchema, TutorialSchema } from './schemas';

type DBTutorial = z.infer<typeof DBTutorialSchema>;
type Tutorial = z.infer<typeof TutorialSchema>;

export const TutorialCodecConfig: TransformerConfig<
  typeof TutorialSchema,
  typeof DBTutorialSchema
> = {
  name: 'Tutorial',
  clientSchema: TutorialSchema,
  dbSchema: DBTutorialSchema,
  transformers: [
    // attendees: Attendee[] ↔ Record<studentId, Attendee>
    createTransformer<typeof TutorialSchema, typeof DBTutorialSchema>({
      encoder: {
        sourceFields: ['attendees'] as const,
        encode: ({ attendees }) => ({
          attendees: Object.fromEntries(
            (attendees as Tutorial['attendees']).map(val => [val.studentId, val])
          ),
        }),
      },
      decoder: {
        sourceFields: ['attendees'] as const,
        decode: ({ attendees }) => ({
          attendees: Object.values((attendees ?? {}) as NonNullable<DBTutorial['attendees']>),
        }),
      },
    }),

    // tutorialId: string ↔ (derived from startDatetimeIdentifier)
    createTransformer<typeof TutorialSchema, typeof DBTutorialSchema>({
      encoder: {
        sourceFields: ['tutorialId', 'startDatetime'] as const,
        encode: ({ tutorialId }) => {
          return {
            startDatetimeIdentifier: tutorialId,
          };
        },
      },
      decoder: {
        sourceFields: ['startDatetimeIdentifier'] as const,
        decode: ({ startDatetimeIdentifier }) => {
          const parts = startDatetimeIdentifier.split('#');

          return {
            startDatetime: Number(parts[0]),
            tutorialId: startDatetimeIdentifier,
          };
        },
      },
    }),
  ],
};

export const encodeTutorialToDb = createEncodeToDbFn(TutorialCodecConfig);
export const decodeTutorialFromDb = createDecodeFromDbFn(TutorialCodecConfig);
